import { useLazyQuery } from '@apollo/client'
import {
  Button,
  Hidden,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import Pagination from '@material-ui/lab/Pagination'
import { matchSorter } from 'match-sorter'
import React, { useEffect, useMemo, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import { MODIFY_POLICIES_QUERY } from '../../graphql/modifyPolicy'
import { getEntityCode, getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import cancelPolicyPagesRoleManagement from '../../staticRoleManagement/cancelPolicyRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import { CancelPolicy } from '../../types/cancelAndModifyPolicies'
import PoliciesFilters from './PoliciesFilters'
import { PoliciesTable } from './PoliciesTable'
import { PolicyTableMobile } from './PolicyTableMobile'
import useStyles from './styles/index.styles'

const ModifyPoliciesList: React.FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [page, setpage] = useState(1)
  const [perPage] = useState(5)
  const [total, settotal] = useState(10)
  const [autoCompleteProvider, setAutoCompleteProvider] = useState<any>()
  const [searchInputValue, setSearchInputValue] = React.useState<string>('')
  const [filteredCancelPolicies, setFilteredCancelPolicies] = React.useState<
    CancelPolicy[]
  >([])

  const [getPolicies, { data, loading, refetch }] = useLazyQuery<{
    modifyPolicies: CancelPolicy[]
  }>(MODIFY_POLICIES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const isRoleProvider = getRoleFromStorage() === ROLE.PROVIDER

  const handleGetPolicies = () => {
    if (isRoleProvider) {
      getPolicies({
        variables: {
          providerCode: getEntityCode()
        }
      })
    }
    if (autoCompleteProvider) {
      getPolicies({
        variables: {
          providerCode: autoCompleteProvider.value
        }
      })
    }
  }

  useEffect(() => {
    if (isRoleProvider || autoCompleteProvider) {
      handleGetPolicies()
    } else {
      setFilteredCancelPolicies([])
    }
  }, [autoCompleteProvider, data])

  useEffect(() => {
    setpage(1)
    const fullpoliciesList = data?.modifyPolicies ? data?.modifyPolicies : []

    const newFilteredPoliciesList = matchSorter(
      fullpoliciesList,
      searchInputValue,
      {
        keys: ['basicDetails.name', 'basicDetails.description']
      }
    )

    if (isRoleProvider || autoCompleteProvider) {
      setFilteredCancelPolicies(newFilteredPoliciesList)
    } else {
      setFilteredCancelPolicies([])
    }

    settotal(newFilteredPoliciesList.length)
  }, [searchInputValue, loading, data])

  const numberOfPages = useMemo(() => Math.ceil(total / perPage), [total])

  const hasAccessToChangeProvider = cancelPolicyPagesRoleManagement
    .find(
      (item) => item.route === '/policies/(cancel-policies|amendment-policies)'
    )
    ?.sections?.PROVIDER_SELECTOR?.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  return (
    <div>
      <PageHeader title={getTranslate('Amendment Policies')} />
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate('Amendment Policies')}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          component={LocalizedLink}
          to={
            cancelPolicyPagesRoleManagement
              .find(
                (item) =>
                  item.route ===
                  '/policies/(cancel-policies|amendment-policies)'
              )
              ?.sections?.ADD_POLICY_BUTTON.rolesListWithWriteAccess?.includes(
                getRoleFromStorage()
              )
              ? '/policies/amendment-policies/create'
              : ''
          }>
          {getTranslate(`Add${isSmallScreen ? '' : ' Amendment Policy'}`)}
        </Button>
      </div>
      <PoliciesFilters
        hasAccessToChangeProvider={hasAccessToChangeProvider}
        inputValue={searchInputValue}
        setSearchInputValue={setSearchInputValue}
        loadingForDisableSearchBox={loading}
        setAutoCompleteProvider={setAutoCompleteProvider}
        autoCompleteProvider={autoCompleteProvider}
      />
      <Hidden smDown>
        <PoliciesTable
          refetch={refetch}
          data={filteredCancelPolicies.slice(
            (page - 1) * perPage,
            page * perPage
          )}
          loading={loading}
          providerCode={autoCompleteProvider && autoCompleteProvider.value}
        />
      </Hidden>
      <Hidden mdUp>
        <PolicyTableMobile
          data={filteredCancelPolicies.slice(
            (page - 1) * perPage,
            page * perPage
          )}
          loading={loading}
          providerCode={autoCompleteProvider && autoCompleteProvider.value}
        />
      </Hidden>
      {!autoCompleteProvider && !isRoleProvider && (
        <div className={classes.noTenantSelected}>
          <Typography variant="h2">
            {getTranslate('Select a provider to see its policies')}
          </Typography>
        </div>
      )}
      {(autoCompleteProvider || isRoleProvider) && (
        <div className={classes.paginationContainer}>
          <Pagination
            count={numberOfPages}
            color="primary"
            size={isSmallScreen ? 'small' : 'medium'}
            page={page}
            onChange={(_, value) => setpage(value)}
          />
        </div>
      )}
    </div>
  )
}

export default ModifyPoliciesList
